/**
 * Betriebsansichten: VPS, Dedicated Server, Monitoring, DNS-Dienste und Protokoll.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Contract, LogEntry, Server, Vps } from '@hosting-sim/core';
import { Link } from 'react-router-dom';
import { contractUptimePercent, migrationOf } from '@hosting-sim/core';
import {
  Badge,
  Button,
  Card,
  Column,
  DataTable,
  EmptyState,
  Field,
  Input,
  Meter,
  Modal,
  Mono,
  Notice,
  PageHeader,
  Select,
  Stat,
  StatGrid,
  StatusDot,
  Tabs,
} from '../components/ui';
import { LineChart, Sparkline } from '../components/charts/Charts';
import { SellDeviceButton } from '../components/SellDevice';
import { useGame, useMetrics, useServerLoad } from '../store/game';
import { api } from '../lib/api';
import { bytes, dec, duration, mbps, money, num, pct100, shortId } from '../lib/format';
import { DeviceStatus, FillCell, LoadCell, STATE_LABEL, STATE_TONE } from './Infrastructure';

// ---------------------------------------------------------------------------
// VPS
// ---------------------------------------------------------------------------

export function VpsPage() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);

  if (!world) return null;
  const instances = Object.values(world.vpsInstances);

  const columns: Column<Vps>[] = [
    { key: 'name', header: 'Instanz', sortValue: (v) => v.name, render: (v) => <Mono className="font-medium">{v.name}</Mono> },
    {
      key: 'state',
      header: 'Zustand',
      sortValue: (v) => v.state,
      render: (v) => <StatusDot tone={STATE_TONE[v.state]} label={STATE_LABEL[v.state]} pulse={v.state === 'booting'} />,
    },
    {
      key: 'customer',
      header: 'Kunde',
      hideOnMobile: true,
      render: (v) => {
        const contract = v.contractId ? world.contracts[v.contractId] : null;
        const customer = contract ? world.customers[contract.customerId] : null;
        return <span className="truncate text-xs">{customer ? (customer.company ?? customer.name) : 'frei'}</span>;
      },
    },
    { key: 'host', header: 'Host', hideOnMobile: true, sortValue: (v) => world.servers[v.hostServerId]?.name ?? '', render: (v) => <span className="text-xs">{world.servers[v.hostServerId]?.name}</span> },
    {
      key: 'spec',
      header: 'Ausstattung',
      render: (v) => (
        <span className="text-xs text-panel-500 dark:text-panel-400">
          {v.vcpu} vCPU · {bytes(v.ramGb)} · {bytes(v.diskGb)}
        </span>
      ),
    },
    {
      key: 'load',
      header: 'CPU-Last',
      align: 'right',
      sortValue: (v) => v.cpuLoad,
      render: (v) => (
        <div className="ml-auto w-24">
          <Meter value={v.cpuLoad} max={1} label={<><span /><span className="tabular">{Math.round(v.cpuLoad * 100)} %</span></>} />
        </div>
      ),
    },
    { key: 'ip', header: 'IPv4', align: 'right', hideOnMobile: true, render: (v) => <Mono>{v.ipv4 ?? '–'}</Mono> },
    {
      /*
       * Der laufende Umzug gehört in die Liste und nicht in eine Detailansicht:
       * Eine Live-Migration dauert Stunden, und wer wissen will, was gerade mit
       * seinen Instanzen passiert, schaut hierher.
       */
      key: 'migration',
      header: 'Umzug',
      align: 'right',
      render: (v) => {
        const job = migrationOf(world, 'vps', v.id);
        if (!job) return <span className="text-2xs text-panel-400">–</span>;
        const target = world.servers[job.targetServerId]?.name ?? '?';
        return (
          <Link to="../migrations" className="text-2xs underline">
            {job.phase === 'queued' ? 'wartet' : `→ ${target}`}
          </Link>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="VPS"
        description="Virtuelle Server auf den VPS-Hosts."
        actions={
          <Link to="../migrations">
            <Button>Umzüge</Button>
          </Link>
        }
      />

      <StatGrid cols={4}>
        <Stat label="Instanzen" value={num(instances.length)} hint={`${instances.filter((v) => v.state === 'online').length} online`} />
        <Stat label="Zugewiesene vCPU" value={num(instances.reduce((s, v) => s + v.vcpu, 0))} />
        <Stat label="Zugewiesener RAM" value={bytes(instances.reduce((s, v) => s + v.ramGb, 0))} />
        <Stat
          label="Freie Hostkapazität"
          value={num(Object.values(world.servers).filter((s) => s.role === 'vps-host').length)}
          hint="VPS-Hosts im Bestand"
        />
      </StatGrid>

      <Card className="mt-4" padded={false}>
        <DataTable columns={columns} rows={instances} rowKey={(v) => v.id} empty="Noch keine VPS-Instanzen. Sie entstehen automatisch beim Verkauf eines VPS-Produkts." />
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Dedicated
// ---------------------------------------------------------------------------

export function Dedicated() {
  const world = useGame((s) => s.world);
  if (!world) return null;

  const dedicated = Object.values(world.servers).filter((s) => s.role === 'dedicated');
  const contracts = Object.values(world.contracts).filter((c) => {
    const product = world.products[c.productId];
    return product?.kind === 'dedicated' || product?.kind === 'colocation';
  });

  const columns: Column<Contract>[] = [
    {
      key: 'customer',
      header: 'Kunde',
      sortValue: (c) => world.customers[c.customerId]?.name ?? '',
      render: (c) => {
        const customer = world.customers[c.customerId];
        return <span className="font-medium">{customer ? (customer.company ?? customer.name) : shortId(c.customerId)}</span>;
      },
    },
    { key: 'product', header: 'Produkt', sortValue: (c) => world.products[c.productId]?.name ?? '', render: (c) => world.products[c.productId]?.name },
    {
      key: 'server',
      header: 'Hardware',
      hideOnMobile: true,
      render: (c) => (c.serverId ? <span className="text-xs">{world.servers[c.serverId]?.name}</span> : c.rackId ? <span className="text-xs">{world.racks[c.rackId]?.name}</span> : '–'),
    },
    { key: 'ip', header: 'IP-Adressen', hideOnMobile: true, render: (c) => <Mono>{c.ipv4[0] ?? '–'}</Mono> },
    {
      key: 'uptime',
      header: 'Verfügbarkeit',
      align: 'right',
      sortValue: (c) => contractUptimePercent(c),
      render: (c) => {
        const uptime = contractUptimePercent(c);
        const product = world.products[c.productId];
        const breached = product && uptime < product.slaUptimePercent;
        return <span className={`tabular text-xs ${breached ? 'text-red-600 dark:text-red-400' : ''}`}>{pct100(uptime, 2)}</span>;
      },
    },
    { key: 'traffic', header: 'Traffic', align: 'right', hideOnMobile: true, sortValue: (c) => c.trafficGb, render: (c) => <span className="tabular text-xs">{bytes(c.trafficGb)}</span> },
    { key: 'price', header: 'Preis/Monat', align: 'right', sortValue: (c) => c.monthlyPrice, render: (c) => <span className="tabular">{money(c.monthlyPrice)}</span> },
  ];

  const serverColumns: Column<Server>[] = [
    { key: 'name', header: 'Server', render: (s) => <span className="font-medium">{s.name}</span> },
    { key: 'state', header: 'Zustand', render: (s) => <DeviceStatus device={s} /> },
    { key: 'spec', header: 'Ausstattung', render: (s) => <span className="text-xs text-panel-500">{s.cpuCores} Kerne · {bytes(s.ramGb)} · {bytes(s.ssdGb + s.hddGb)}</span> },
    { key: 'assigned', header: 'Vergeben', align: 'center', render: (s) => (s.contractIds.length > 0 ? <Badge tone="success">belegt</Badge> : <Badge tone="muted">frei</Badge>) },
  ];

  return (
    <>
      <PageHeader title="Dedicated Server und Colocation" description="Exklusiv vergebene Hardware und Rackplätze." />

      <StatGrid cols={3}>
        <Stat label="Verträge" value={num(contracts.filter((c) => c.state === 'active').length)} />
        <Stat label="Dedizierte Hardware" value={num(dedicated.length)} hint={`${dedicated.filter((s) => s.contractIds.length === 0).length} frei`} />
        <Stat label="Umsatz/Monat" value={money(contracts.filter((c) => c.state === 'active').reduce((s, c) => s + c.monthlyPrice, 0))} />
      </StatGrid>

      <Card className="mt-4" title="Verträge" padded={false}>
        <DataTable columns={columns} rows={contracts} rowKey={(c) => c.id} empty="Keine Dedicated- oder Colocation-Verträge." />
      </Card>

      <Card className="mt-4" title="Verfügbare Hardware" padded={false}>
        <DataTable columns={serverColumns} rows={dedicated} rowKey={(s) => s.id} empty="Keine Server mit der Rolle „Dedicated“." />
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Monitoring
// ---------------------------------------------------------------------------

export function Monitoring() {
  const metrics = useMetrics();
  const live = useGame((s) => s.live);
  const world = useGame((s) => s.world);
  const [tab, setTab] = useState('network');

  if (!metrics) return null;
  const monitoring = live?.monitoring ?? world?.monitoring;
  const series = monitoring?.series ?? {};
  const probes = monitoring?.probes ?? [];

  return (
    <>
      <PageHeader title="Monitoring" description="Live-Messwerte aus Netz, Systemen und Betrieb." />

      <StatGrid cols={5}>
        <Stat label="Durchsatz" value={mbps(metrics.derived.traffic.totalMbps)} />
        <Stat label="Latenz" value={`${dec(metrics.derived.quality.avgLatencyMs, 1)} ms`} />
        <Stat
          label="Paketverlust"
          value={pct100(metrics.derived.quality.packetLossPercent, 2)}
          tone={metrics.derived.quality.packetLossPercent > 1 ? 'negative' : 'positive'}
        />
        <Stat label="Verfügbarkeit" value={pct100(metrics.derived.quality.uptimePercent, 2)} />
        <Stat
          label="Erreichbarkeit"
          value={pct100(metrics.derived.internetReachability * 100, 0)}
          tone={metrics.derived.internetReachability > 0.98 ? 'positive' : 'negative'}
        />
      </StatGrid>

      <div className="mt-4">
        <Tabs
          tabs={[
            { id: 'network', label: 'Netz' },
            { id: 'systems', label: 'Systeme' },
            { id: 'business', label: 'Geschäft' },
            { id: 'probes', label: 'Erreichbarkeitstests' },
          ]}
          active={tab}
          onChange={setTab}
        />
      </div>

      {tab === 'network' && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <Card title="Verkehr">
            <LineChart
              series={[
                { label: 'Gesamt', points: series.traffic?.points ?? [], color: '#21a471' },
                { label: 'Transit', points: series.transit?.points ?? [], color: '#f59e0b' },
                { label: 'Peering', points: series.peering?.points ?? [], color: '#3b82f6' },
              ]}
              unit="Mbit/s"
            />
          </Card>
          <Card title="Qualität">
            <LineChart
              series={[
                { label: 'Latenz (ms)', points: series.latency?.points ?? [], color: '#8b5cf6' },
                { label: 'Paketverlust (%)', points: series.loss?.points ?? [], color: '#ef4444' },
              ]}
            />
          </Card>
          <Card title="Interneterreichbarkeit" className="lg:col-span-2">
            <LineChart series={[{ label: 'Erreichbarkeit (%)', points: series.reachability?.points ?? [], color: '#21a471' }]} height={160} />
          </Card>
        </div>
      )}

      {tab === 'systems' && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <Card title="Auslastung">
            <LineChart
              series={[
                { label: 'CPU (%)', points: series.cpu?.points ?? [], color: '#3b82f6' },
                { label: 'RAM (%)', points: series.ram?.points ?? [], color: '#8b5cf6' },
              ]}
            />
          </Card>
          <Card title="Temperatur und Leistung">
            <LineChart
              series={[
                { label: 'Ø Temperatur (°C)', points: series.temperature?.points ?? [], color: '#f59e0b' },
                { label: 'Leistungsaufnahme (kW)', points: series.power?.points ?? [], color: '#14b8a6' },
              ]}
            />
          </Card>
          <Card
            title="Auslastung je Server"
            subtitle="Belegung gegen die Verkaufsgrenze, daneben die momentane Rechenlast"
            className="lg:col-span-2"
            padded={false}
          >
            <ServerLoadTable />
          </Card>
          <Card title="Geräte mit Auffälligkeiten" className="lg:col-span-2" padded={false}>
            <DeviceHealthTable />
          </Card>
        </div>
      )}

      {tab === 'business' && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <Card title="Kunden und Zufriedenheit">
            <LineChart
              series={[
                { label: 'Kunden', points: series.customers?.points ?? [], color: '#21a471' },
                { label: 'Zufriedenheit', points: series.satisfaction?.points ?? [], color: '#3b82f6' },
              ]}
            />
          </Card>
          <Card title="Liquidität">
            <LineChart series={[{ label: 'Liquidität (€)', points: series.cash?.points ?? [], color: '#f59e0b' }]} />
          </Card>
        </div>
      )}

      {tab === 'probes' && (
        <Card className="mt-4" title="Messungen zu externen Zielen" subtitle="Round-Trip-Zeit und Paketverlust aus dem eigenen Netz">
          {probes.length === 0 ? (
            <EmptyState title="Noch keine Messwerte" description="Die Messungen laufen, sobald die Simulation einige Ticks gerechnet hat." />
          ) : (
            <div className="space-y-2">
              {probes.map((probe) => (
                <div key={probe.target} className="surface-muted flex flex-wrap items-center gap-3 p-3">
                  <StatusDot tone={probe.reachable ? (probe.lossPercent > 1 ? 'warning' : 'success') : 'danger'} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{probe.description}</p>
                    <Mono className="text-panel-500">{probe.target}</Mono>
                  </div>
                  <Field label="Latenz">{probe.reachable ? `${dec(probe.latencyMs, 1)} ms` : '–'}</Field>
                  <Field label="Verlust">{pct100(probe.lossPercent, 1)}</Field>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </>
  );
}

/**
 * Auslastung aller Server nebeneinander.
 *
 * Die Serverseite listet den Bestand, hier geht es um den Verlauf des Betriebs:
 * Der am stärksten belegte Knoten steht oben, weil er derjenige ist, der als
 * Nächstes einen Auftrag ablehnt.
 */
function ServerLoadTable() {
  const world = useGame((s) => s.world);
  const load = useServerLoad();
  if (!world) return null;

  const servers = Object.values(world.servers).sort((a, b) => (load.get(b.id)?.fill ?? 0) - (load.get(a.id)?.fill ?? 0));

  const columns: Column<Server>[] = [
    {
      key: 'name',
      header: 'Server',
      sortValue: (s) => s.name,
      render: (s) => (
        <div>
          <p className="font-medium">{s.name}</p>
          <p className="text-2xs text-panel-500">{world.datacenters[s.datacenterId]?.city}</p>
        </div>
      ),
    },
    { key: 'state', header: 'Zustand', sortValue: (s) => s.state, render: (s) => <DeviceStatus device={s} /> },
    {
      key: 'fill',
      header: 'Belegung',
      width: '10rem',
      sortValue: (s) => load.get(s.id)?.fill ?? -1,
      render: (s) => <FillCell row={load.get(s.id)} />,
    },
    {
      key: 'load',
      header: 'Rechenlast',
      width: '10rem',
      hideOnMobile: true,
      sortValue: (s) => load.get(s.id)?.load ?? -1,
      render: (s) => <LoadCell row={load.get(s.id)} />,
    },
    {
      key: 'services',
      header: 'Dienste',
      align: 'right',
      hideOnMobile: true,
      sortValue: (s) => load.get(s.id)?.services ?? 0,
      render: (s) => <span className="tabular text-xs">{num(load.get(s.id)?.services ?? 0)}</span>,
    },
  ];

  return <DataTable columns={columns} rows={servers} rowKey={(s) => s.id} dense empty="Noch keine Server vorhanden." />;
}

function DeviceHealthTable() {
  const world = useGame((s) => s.world);
  if (!world) return null;

  const devices = [
    ...Object.values(world.servers).map((d) => ({ ...d, kind: 'Server' })),
    ...Object.values(world.routers).map((d) => ({ ...d, kind: 'Router' })),
    ...Object.values(world.switches).map((d) => ({ ...d, kind: 'Switch' })),
    ...Object.values(world.firewalls).map((d) => ({ ...d, kind: 'Firewall' })),
  ].filter((d) => d.state !== 'online' || d.health < 0.6 || d.temperatureC > 45);

  const columns: Column<(typeof devices)[number]>[] = [
    { key: 'name', header: 'Gerät', render: (d) => <span className="font-medium">{d.name}</span> },
    { key: 'kind', header: 'Art', render: (d) => <Badge tone="muted">{d.kind}</Badge> },
    { key: 'state', header: 'Zustand', render: (d) => <StatusDot tone={STATE_TONE[d.state]} label={STATE_LABEL[d.state]} /> },
    {
      key: 'health',
      header: 'Restzustand',
      align: 'right',
      sortValue: (d) => d.health,
      render: (d) => <span className={`tabular text-xs ${d.health < 0.4 ? 'text-red-600 dark:text-red-400' : ''}`}>{Math.round(d.health * 100)} %</span>,
    },
    {
      key: 'temp',
      header: 'Temperatur',
      align: 'right',
      sortValue: (d) => d.temperatureC,
      render: (d) => <span className={`tabular text-xs ${d.temperatureC > 60 ? 'text-red-600 dark:text-red-400' : d.temperatureC > 45 ? 'text-amber-600' : ''}`}>{Math.round(d.temperatureC)} °C</span>,
    },
  ];

  return <DataTable columns={columns} rows={devices} rowKey={(d) => d.id} dense empty="Alle Geräte laufen im grünen Bereich." />;
}

// ---------------------------------------------------------------------------
// Dienste (DNS, VLAN, VRF, Firewall)
// ---------------------------------------------------------------------------

export function Services() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [tab, setTab] = useState('dns');
  const [zoneName, setZoneName] = useState('');
  const [vlanTag, setVlanTag] = useState('200');
  const [vlanName, setVlanName] = useState('Kundennetz');

  if (!world) return null;
  const zones = Object.values(world.dnsZones);
  const vlans = Object.values(world.vlans);
  const vrfs = Object.values(world.vrfs);
  const firewalls = Object.values(world.firewalls);
  const dnsServers = Object.values(world.servers).filter((s) => s.role === 'dns' || s.role === 'shared-hosting');
  const datacenters = Object.values(world.datacenters);

  return (
    <>
      <PageHeader title="Netzwerkdienste" description="DNS, VLANs, VRFs und Paketfilter." />

      <Tabs
        tabs={[
          { id: 'dns', label: 'DNS', badge: <Badge tone="muted">{zones.length}</Badge> },
          { id: 'vlan', label: 'VLAN / VRF', badge: <Badge tone="muted">{vlans.length}</Badge> },
          { id: 'firewall', label: 'Firewall', badge: <Badge tone="muted">{firewalls.length}</Badge> },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'dns' && (
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <Card title="Zonen" className="lg:col-span-2" padded={false}>
            <DataTable
              columns={[
                { key: 'name', header: 'Zone', render: (z: (typeof zones)[number]) => <Mono className="font-medium">{z.name}</Mono> },
                {
                  key: 'type',
                  header: 'Art',
                  render: (z: (typeof zones)[number]) => <Badge tone={z.reverseForPrefixId ? 'info' : 'neutral'}>{z.reverseForPrefixId ? 'Reverse' : 'Forward'}</Badge>,
                },
                { key: 'records', header: 'Einträge', align: 'right', render: (z: (typeof zones)[number]) => <span className="tabular text-xs">{z.records.length}</span> },
                {
                  key: 'servers',
                  header: 'Nameserver',
                  align: 'right',
                  render: (z: (typeof zones)[number]) => {
                    const up = z.serverIds.filter((id) => world.servers[id]?.state === 'online').length;
                    return <StatusDot tone={up > 0 ? 'success' : 'danger'} label={`${up} / ${z.serverIds.length} erreichbar`} />;
                  },
                },
              ]}
              rows={zones}
              rowKey={(z) => z.name}
              empty="Noch keine DNS-Zone angelegt."
            />
          </Card>

          <Card title="Zone anlegen">
            <div className="space-y-3">
              <Input label="Zonenname" value={zoneName} onChange={(e) => setZoneName(e.target.value)} placeholder="beispiel.de" />
              <Button
                variant="primary"
                disabled={!zoneName || dnsServers.length === 0}
                onClick={async () => {
                  const result = await run({ type: 'dns/createZone', name: zoneName, serverIds: dnsServers.slice(0, 2).map((s) => s.id) });
                  if (result.ok) setZoneName('');
                }}
              >
                Anlegen
              </Button>
              {dnsServers.length === 0 && <Notice tone="warning">Es wird mindestens ein Server benötigt, der die Zone ausliefert.</Notice>}
              <Notice tone="info">
                Reverse-Zonen werden im Bereich „IP-Adressen“ je Präfix delegiert. Ohne Reverse-DNS lehnen viele Empfänger die Mails der
                Kunden ab.
              </Notice>
            </div>
          </Card>
        </div>
      )}

      {tab === 'vlan' && (
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <Card title="VLANs" className="lg:col-span-2" padded={false}>
            <DataTable
              columns={[
                { key: 'tag', header: 'Tag', align: 'right', render: (v: (typeof vlans)[number]) => <Mono className="font-medium">{v.tag}</Mono> },
                { key: 'name', header: 'Name', render: (v: (typeof vlans)[number]) => v.name },
                { key: 'dc', header: 'Standort', render: (v: (typeof vlans)[number]) => world.datacenters[v.datacenterId]?.city },
                { key: 'prefixes', header: 'Netze', align: 'right', render: (v: (typeof vlans)[number]) => <span className="tabular text-xs">{v.prefixIds.length}</span> },
                { key: 'dhcp', header: 'DHCP', align: 'center', render: (v: (typeof vlans)[number]) => (v.dhcp ? <Badge tone="success">aktiv</Badge> : <Badge tone="muted">aus</Badge>) },
              ]}
              rows={vlans}
              rowKey={(v) => v.id}
              empty="Keine VLANs angelegt."
            />
          </Card>

          <Card title="VLAN anlegen">
            <div className="space-y-3">
              <Input label="VLAN-Tag" value={vlanTag} onChange={(e) => setVlanTag(e.target.value.replace(/\D/g, ''))} hint="1 bis 4094" />
              <Input label="Bezeichnung" value={vlanName} onChange={(e) => setVlanName(e.target.value)} />
              <Button
                variant="primary"
                disabled={datacenters.length === 0}
                onClick={() =>
                  void run({ type: 'vlan/create', datacenterId: datacenters[0].id, tag: Number(vlanTag) || 1, name: vlanName })
                }
              >
                Anlegen
              </Button>

              {vrfs.length > 0 && (
                <div className="border-t divider pt-3">
                  <p className="label mb-1.5">VRFs</p>
                  <ul className="space-y-1">
                    {vrfs.map((vrf) => (
                      <li key={vrf.id} className="text-xs">
                        <Mono className="font-medium">{vrf.name}</Mono> <span className="text-panel-500">RD {vrf.rd}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {tab === 'firewall' && (
        <div className="mt-4 space-y-4">
          {firewalls.length === 0 && (
            <EmptyState
              title="Keine Firewall vorhanden"
              description="Ohne Filterinfrastruktur treffen volumetrische Angriffe die Uplinks ungebremst. Eine Scrubbing-Appliance lässt sich im Serverbereich beschaffen."
            />
          )}
          {firewalls.map((firewall) => (
            <Card
              key={firewall.id}
              title={firewall.name}
              subtitle={`${mbps(firewall.throughputMbps)} Durchsatz · ${firewall.scrubbingGbps} Gbit/s Scrubbing`}
              actions={
                <>
                  <DeviceStatus device={firewall} />
                  <SellDeviceButton kind="firewall" id={firewall.id} name={firewall.name} />
                </>
              }
            >
              <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Field label="Sitzungen">
                  {num(firewall.currentSessions)} / {num(firewall.sessionCapacity)}
                </Field>
                <Field label="Standort">{world.datacenters[firewall.datacenterId]?.city}</Field>
                <Field label="Regeln">{firewall.rules.length}</Field>
                <Field label="Zustand">{Math.round(firewall.health * 100)} %</Field>
              </div>
              <div className="space-y-1">
                {firewall.rules.map((rule) => (
                  <div key={rule.seq} className="surface-muted flex items-center gap-2 p-2 text-xs">
                    <span className="w-8 font-mono text-panel-500">{rule.seq}</span>
                    <Badge tone={rule.action === 'allow' ? 'success' : rule.action === 'deny' ? 'danger' : 'warning'}>{rule.action}</Badge>
                    <Mono className="min-w-0 flex-1 truncate">
                      {rule.protocol} {rule.source} → {rule.destination}
                      {rule.port ? `:${Array.isArray(rule.port) ? rule.port.join('-') : rule.port}` : ''}
                    </Mono>
                    <span className="text-panel-500">{rule.description}</span>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Protokoll
// ---------------------------------------------------------------------------

const CATEGORY_LABEL: Record<string, string> = {
  system: 'System',
  network: 'Netz',
  bgp: 'BGP',
  billing: 'Abrechnung',
  customer: 'Kunden',
  hardware: 'Hardware',
  security: 'Sicherheit',
  event: 'Ereignis',
  research: 'Forschung',
  ai: 'KI',
};

export function Logs() {
  const gameId = useGame((s) => s.gameId);
  const tick = useGame((s) => s.live?.meta.tick ?? s.world?.meta.tick);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('');

  useEffect(() => {
    if (!gameId) return;
    void api
      .logs(gameId, { category: category || undefined, severity: severity || undefined, limit: 500 })
      .then(setLogs)
      .catch(() => setLogs([]));
  }, [gameId, category, severity, tick]);

  const columns: Column<LogEntry>[] = [
    { key: 'tick', header: 'Tick', align: 'right', width: '6rem', sortValue: (l) => l.tick, render: (l) => <span className="tabular text-xs text-panel-500">{num(l.tick)}</span> },
    {
      key: 'severity',
      header: 'Stufe',
      width: '6rem',
      render: (l) => (
        <Badge tone={l.severity === 'critical' ? 'danger' : l.severity === 'error' ? 'danger' : l.severity === 'warning' ? 'warning' : 'muted'}>
          {l.severity}
        </Badge>
      ),
    },
    { key: 'category', header: 'Bereich', width: '8rem', hideOnMobile: true, render: (l) => <span className="text-xs">{CATEGORY_LABEL[l.category] ?? l.category}</span> },
    { key: 'message', header: 'Meldung', render: (l) => <span className="text-xs">{l.message}</span> },
  ];

  return (
    <>
      <PageHeader
        title="Protokoll"
        description="Vollständige Ereignishistorie des Betriebs."
        actions={
          <>
            <Select value={category} onChange={(e) => setCategory(e.target.value)} className="w-auto">
              <option value="">Alle Bereiche</option>
              {Object.entries(CATEGORY_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Select value={severity} onChange={(e) => setSeverity(e.target.value)} className="w-auto">
              <option value="">Alle Stufen</option>
              <option value="info">Info</option>
              <option value="warning">Warnung</option>
              <option value="error">Fehler</option>
              <option value="critical">Kritisch</option>
            </Select>
          </>
        }
      />

      <Card padded={false}>
        <DataTable columns={columns} rows={logs} rowKey={(l) => l.id} dense maxHeight="max-h-[70vh]" empty="Keine Einträge für diesen Filter." />
      </Card>
    </>
  );
}
